import { config } from "../config";

export type PlaylistItem = {
  title: string;
  src: string;
  type?: string;
  poster?: string;
  durationSec?: number;
  fallbackSrc?: string;
};

export type PlaylistManifest = {
  title?: string;
  items: PlaylistItem[];
};

export async function loadPlaylist(): Promise<PlaylistManifest> {
  const gatewayPlaylistUrl = `${config.gatewayUrl.replace(/\/$/, "")}/watch-playlist.json`;
  const playlistUrl = config.channelPlaylistUrl || gatewayPlaylistUrl || "/watch-playlist.json";
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = window.setTimeout(() => controller?.abort(), 5000);
  try {
    const res = await fetch(playlistUrl, {
      cache: "no-store",
      signal: controller?.signal,
    });
    if (!res.ok) return { items: [] };
    const data = (await res.json()) as Partial<PlaylistManifest>;
    if (!Array.isArray(data.items)) return { items: [] };

    const items = data.items
      .map(normalizeItem)
      .filter((item): item is PlaylistItem => item !== null);

    return {
      title: typeof data.title === "string" ? data.title : undefined,
      items,
    };
  } catch {
    return { items: [] };
  } finally {
    window.clearTimeout(timeout);
  }
}

function normalizeItem(raw: unknown): PlaylistItem | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  const title = typeof item.title === "string" && item.title.trim() ? item.title.trim() : null;
  const src = typeof item.src === "string" && item.src.trim() ? item.src.trim() : null;
  if (!title || !src) return null;
  return {
    title,
    src,
    type: typeof item.type === "string" && item.type.trim() ? item.type.trim() : undefined,
    poster: typeof item.poster === "string" && item.poster.trim() ? item.poster.trim() : undefined,
    durationSec: typeof item.durationSec === "number" && item.durationSec > 0 ? item.durationSec : undefined,
    fallbackSrc: typeof item.fallbackSrc === "string" && item.fallbackSrc.trim() ? item.fallbackSrc.trim() : undefined,
  };
}

export function isHlsSource(item: PlaylistItem): boolean {
  return item.type === "application/vnd.apple.mpegurl" || /\.m3u8([?#].*)?$/.test(item.src);
}
